import { Plugin } from "@fuse/recoil/plugins"
import { PluginApprovalStatus } from "@fuse/recoil/plugins"
import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { getRemoteConfigValue } from "@firebaseConfig/remoteConfigUtil"

/**
 * Gets the transpiled content for a plugin based on user role
 * @param plugin - The plugin to get content from
 * @param role - The user role (guest, host, )
 * @returns The transpiled content or undefined if not available
 */
export function getTranspiledContent(
  plugin: Plugin | null | undefined,
  role: UserRole,
): string | undefined {
  if (!plugin || !plugin.code) {
    return undefined
  }

  switch (role) {
    case UserRole.GUEST:
      return plugin.code.transpiledFrontendGuestCodeWeb
    case UserRole.HOST_EVENT_VIEW:
      // Fall back to guest code if host code is not available
      return (
        plugin.code.transpiledFrontendHostViewCodeWeb ||
        plugin.code.transpiledFrontendGuestCodeWeb
      )
    case UserRole.HOST_CONFIG_VIEW:
      // Fall back to host code, then guest code if  code is not available
      return (
        plugin.code.transpiledFrontendHostConfigCodeWeb ||
        plugin.code.transpiledFrontendHostViewCodeWeb ||
        plugin.code.transpiledFrontendGuestCodeWeb
      )
    default:
      return undefined
  }
}

/**
 * Checks if a plugin qualifies for instant add/remove functionality
 * Must be both zero-config and lets-hang-owned
 */
export const isInstantPlugin = (plugin: any): boolean => {
  const requiresZeroConfig =
    plugin?.manifest?.requiresZeroConfiguration === true
  const isLetsHangOwned =
    plugin?.manifest?.approvalStatus === PluginApprovalStatus.LETS_HANG_OWNED
  const result = requiresZeroConfig && isLetsHangOwned

  // Debug logging
  console.log("[isInstantPlugin] Debug:", {
    pluginId: plugin?.id,
    pluginName: plugin?.manifest?.name,
    requiresZeroConfiguration: plugin?.manifest?.requiresZeroConfiguration,
    requiresZeroConfigCheck: requiresZeroConfig,
    approvalStatus: plugin?.manifest?.approvalStatus,
    expectedApprovalStatus: PluginApprovalStatus.LETS_HANG_OWNED,
    isLetsHangOwnedCheck: isLetsHangOwned,
    finalResult: result,
  })

  return result
}

/**
 * Gets the appropriate icon for a plugin based on its status
 */
export const getPluginStatusIcon = (plugin: any): string => {
  if (isInstantPlugin(plugin)) {
    return "⚡" // Lightning bolt for instant plugins
  }

  switch (plugin?.manifest?.approvalStatus) {
    case PluginApprovalStatus.CERTIFIED:
      return "✅"
    case PluginApprovalStatus.REVIEW_APPROVED:
      return "👍"
    case PluginApprovalStatus.AWAITING_REVIEW:
      return "⏳"
    case PluginApprovalStatus.REVIEW_DENIED:
      return "❌"
    case PluginApprovalStatus.PRIVATE:
      return "🔒"
    default:
      return "🔧"
  }
}

/**
 * Creates an example zero-config plugin manifest for testing
 */
export const createExampleZeroConfigPlugin = (
  name: string,
  description: string,
) => ({
  name,
  description,
  version: "1.0.0",
  author: "Let's Hang",
  requiresZeroConfiguration: true,
  approvalStatus: PluginApprovalStatus.LETS_HANG_OWNED,
  searchTags: ["instant", "zero-config"],
})

/**
 * Fetch plugin list from Firebase Remote Config (key: static_plugin_list)
 */
export async function fetchPluginListFromRemoteConfig(): Promise<Plugin[]> {
  try {
    const value = getRemoteConfigValue("static_plugin_list")
    if (!value) return []
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    console.error(
      "Failed to fetch or parse static_plugin_list from Remote Config",
      e,
    )
    return []
  }
}
